import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NotificationService } from './shared/services/notification.service';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { CurrentUserService } from './shared/services/current-user.service';
import { SignatureVerifierService } from './shared/services/signature-verifier.service';
import { IConfigData } from './shared/interfaces/config-data.interface';
import { Constants } from './shared/enums/constants.enum';
import { APP_CONFIG } from './app.tokens';
import { SettingsService } from './shared/services/settings.service';

/**
 * Root application component responsible for:
 * - Bootstrapping initial configuration and cryptographic public key retrieval.
 * - Triggering secure configuration loading from the backend.
 * - Signaling when configuration is fully loaded so the rest of the app can proceed.
 * - Initiating retrieval of current user / MSAL (authentication) related data.
 *
 * Lifecycle:
 * 1. On initialization (ngOnInit):
 *    a. Fetches the API public key (used for signature / encryption validation).
 *    b. Stores the key in SettingsService.
 *    c. Proceeds to load the secure configuration payload.
 * 2. getData():
 *    a. Builds the configuration endpoint URL.
 *    b. Performs a signed / verified GET via SignatureVerifierService.
 *    c. Persists the configuration in SettingsService.
 *    d. Flags that initial data load is complete.
 *    e. Triggers MSAL user context retrieval.
 *
 * Notes:
 * - Relies on injected APP_CONFIG for the base API URL.
 * - The isLoadData signal in SettingsService acts as a readiness indicator for downstream consumers.
 */

/** Service used to display user notifications (e.g., errors, info). */

/** Service exposing current authenticated user state and triggering MSAL data retrieval. */

/** Service encapsulating signature verification / secure HTTP GET operations. */

/** Service holding application-wide settings, configuration data, and readiness signals. */

/** Global application configuration object (injected via APP_CONFIG token). */

/** Reactive signal indicating whether initial configuration data has been loaded. */

/**
 * Angular lifecycle hook: initiates public key retrieval before loading secure configuration.
 * Ensures the cryptographic public key is available before attempting to verify signed responses.
 */

/**
 * Retrieves the remote configuration object:
 * - Constructs the endpoint URL from the injected config.
 * - Performs a typed, signature-verified request.
 * - Persists the configuration and marks the data load as complete.
 * - Triggers MSAL user data acquisition after successful config load.
 *
 * Error Handling:
 * (Not currently implemented) Consider adding .catch(...) to handle network or signature errors
 * and surfacing them via NotificationService.
 *
 * Side Effects:
 * - Mutates SettingsService state (public key, config data, isLoadData flag).
 * - Kicks off user context retrieval (CurrentUserService).
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, TranslateModule, IndicatorsModule, InputsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  notificationService = inject(NotificationService);
  currentUserService = inject(CurrentUserService);
  private signatureVerifierService = inject(SignatureVerifierService);
  private settingsService = inject(SettingsService);
  config = inject(APP_CONFIG);
  isLoadData = this.settingsService.isLoadData;

  ngOnInit(): void {
    this.settingsService.getAPIPublicKey().then((key: string) => {
      this.settingsService.setPublicKey(key);
      this.getData();
    });
  }

  getData(): void {
    const url = `${this.config.apiUrl}/${Constants.SETTINGS}/config`;
    this.signatureVerifierService
      .get<IConfigData>(url)
      .then((data: IConfigData) => {
        this.settingsService.setConfigData(data);
        this.settingsService.isLoadData.set(true);
        this.currentUserService.getMsalData();
      });
  }
}
